<template>
  <div class="repriced-locks-page">
    <template v-if="isAuthorized">
      <div class="page-header">
        <div class="page-header-left">
          <div class="page-icon">
            <i class="fa-duotone fa-calendar-plus"></i>
          </div>

          <div>
            <h1 class="page-title">Lock Extensions Review</h1>
            <p class="page-subtitle">Extension requests, recommendations, and queue activity</p>
          </div>
        </div>

        <div class="header-actions">
          <button class="alt-orgs-btn" @click="openAltOrgModal" :disabled="altOrgLoading">
            {{ altOrgLoading ? 'Loading...' : 'Manage Orgs' }}
          </button>

          <select v-model="timeFrame" class="filter-select days-select" @change="loadData">
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
          </select>

          <button class="refresh-btn" @click="loadData" :disabled="loading">
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>

      <div class="summary-sections">
        <div class="summary-section">
          <div class="section-header">
            <div class="section-title-wrap">
              <i class="fa-duotone fa-list-check section-icon"></i>
              <span class="section-title">Request Overview</span>
            </div>
          </div>

          <div class="section-cards">
            <div class="summary-card">
              <div class="summary-card-header">
                <i class="fa-duotone fa-table-rows summary-icon"></i>
                <span class="summary-label">Total</span>
              </div>
              <div class="summary-value">{{ summary.total }}</div>
            </div>

            <div class="summary-card">
              <div class="summary-card-header">
                <i class="fa-duotone fa-hourglass-half summary-icon"></i>
                <span class="summary-label">Pending</span>
              </div>
              <div class="summary-value">{{ summary.pending }}</div>
            </div>

            <div class="summary-card">
              <div class="summary-card-header">
                <i class="fa-duotone fa-badge-check summary-icon"></i>
                <span class="summary-label">Processed</span>
              </div>
              <div class="summary-value">{{ summary.processed }}</div>
            </div>
          </div>
        </div>

        <div class="summary-section decisions-section">
          <div class="section-header">
            <div class="section-title-wrap">
              <i class="fa-duotone fa-sparkles section-icon"></i>
              <span class="section-title">Recommendations</span>
            </div>
          </div>

          <div class="section-cards">
            <div class="summary-card approve">
              <div class="summary-card-header">
                <i class="fa-duotone fa-circle-check summary-icon"></i>
                <span class="summary-label">Approve</span>
              </div>
              <div class="summary-value">{{ summary.approved }}</div>
            </div>

            <div class="summary-card denied">
              <div class="summary-card-header">
                <i class="fa-duotone fa-circle-xmark summary-icon"></i>
                <span class="summary-label">Deny</span>
              </div>
              <div class="summary-value">{{ summary.denied }}</div>
            </div>

            <div class="summary-card manual-review">
              <div class="summary-card-header">
                <i class="fa-duotone fa-user-check summary-icon"></i>
                <span class="summary-label">Manual Review</span>
              </div>
              <div class="summary-value">{{ summary.manual_review }}</div>
            </div>

            <div class="summary-card exception-needed">
              <div class="summary-card-header">
                <i class="fa-duotone fa-file-circle-question summary-icon"></i>
                <span class="summary-label">Exception Needed</span>
              </div>
              <div class="summary-value">{{ summary.exception_needed }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="toolbar single-line-toolbar">
        <input v-model.trim="search"
               type="text"
               class="search-input"
               placeholder="Search loan number, borrower, org, LO, recommendation, status, channel" />

        <select v-model="decisionFilter" class="filter-select toolbar-select">
          <option value="">All Recommendations</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="manual_review">Manual Review</option>
          <option value="exception_needed">Exception Needed</option>
          <option value="request_only">Request Only</option>
          <option value="no_recommendation">No Recommendation Yet</option>
        </select>

        <select v-model="processedFilter" class="filter-select toolbar-select">
          <option value="">All Queue Status</option>
          <option value="processed">Processed</option>
          <option value="pending">Pending</option>
        </select>

        <select v-model="channelFilter" class="filter-select toolbar-select">
          <option value="">All Channels</option>
          <option v-for="item in channelOptions" :key="item" :value="item">
            {{ displayChannel(item) }}
          </option>
        </select>
      </div>

      <div class="table-card">
        <div class="table-card-header">
          <div class="table-card-title-wrap">
            <i class="fa-duotone fa-calendar-clock table-card-icon"></i>
            <span class="table-card-title">Lock Extension Queue</span>
          </div>

          <div class="table-card-count">
            {{ filteredRows.length }}
          </div>
        </div>

        <div v-if="loading" class="table-state">Loading lock extension requests...</div>
        <div v-else-if="errorMessage" class="table-state error">{{ errorMessage }}</div>
        <div v-else-if="pagedRows.length === 0" class="table-state">No lock extension requests found.</div>

        <div v-else class="table-wrap">
          <table class="locks-table">
            <thead>
              <tr>
                <th>Exclude</th>
                <th @click="setSort('request_date')">
                  Request Date
                  <span>{{ sortIcon('request_date') }}</span>
                </th>
                <th @click="setSort('loan_number')">
                  Loan
                  <span>{{ sortIcon('loan_number') }}</span>
                </th>
                <th @click="setSort('borrower_last_name')">
                  Borrower
                  <span>{{ sortIcon('borrower_last_name') }}</span>
                </th>
                <th @click="setSort('orgid')">
                  Org
                  <span>{{ sortIcon('orgid') }}</span>
                </th>
                <th @click="setSort('channel')">
                  Channel
                  <span>{{ sortIcon('channel') }}</span>
                </th>
                <th @click="setSort('current_lock_expiration')">
                  Current Exp
                  <span>{{ sortIcon('current_lock_expiration') }}</span>
                </th>
                <th @click="setSort('requested_extension_days')">
                  Days
                  <span>{{ sortIcon('requested_extension_days') }}</span>
                </th>
                <th @click="setSort('new_expiration')">
                  New Exp
                  <span>{{ sortIcon('new_expiration') }}</span>
                </th>
                <th @click="setSort('loan_officer_name')">
                  LO
                  <span>{{ sortIcon('loan_officer_name') }}</span>
                </th>
                <th @click="setSort('decision')">
                  Recommendation
                  <span>{{ sortIcon('decision') }}</span>
                </th>
                <th @click="setSort('manual_status')">
                  Manual Status
                  <span>{{ sortIcon('manual_status') }}</span>
                </th>
                <th @click="setSort('is_processed')">
                  Queue Status
                  <span>{{ sortIcon('is_processed') }}</span>
                </th>
                <th @click="setSort('has_notes')">
                  Notes
                  <span>{{ sortIcon('has_notes') }}</span>
                </th>
                <th>Summary</th>
                <th>Polly</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in pagedRows"
                  :key="getQueueId(row)"
                  :class="{ 'excluded-row': row.is_excluded }">
                <td>
                  <label class="exclude-check-wrap">
                    <input type="checkbox"
                           :checked="!!row.is_excluded"
                           :disabled="row.exclude_loading"
                           @change="toggleExclude(row, $event.target.checked)" />
                    <span class="exclude-check-label">{{ row.is_excluded ? 'Excluded' : '' }}</span>
                  </label>
                </td>

                <td>{{ formatDateTime(getQueueDate(row)) }}</td>
                <td><div class="primary-text">{{ row.loan_number || '—' }}</div></td>
                <td><div class="primary-text">{{ borrowerName(row) }}</div></td>
                <td>{{ row.orgid || row.orgId || row.org_id || row.alt_org || row.encompass_id || '—' }}</td>
                <td class="compact-cell">{{ displayChannel(row.channel) }}</td>
                <td>{{ formatDateTime(getCurrentExpiration(row)) }}</td>
                <td>{{ getRequestedExtensionDays(row) }}</td>
                <td>{{ formatDateTime(getNewExpiration(row)) }}</td>
                <td>{{ row.loan_officer_name || row.loan_officer || row.loan_officer_email || '—' }}</td>

                <td>
                  <div class="decision-cell">
                    <button class="decision-pill decision-pill-button"
                            :class="decisionClass(normalizedDecision(row))"
                            :disabled="row.manual_status_loading"
                            @click="openDecisionActionModal(row)">
                      {{ decisionLabel(normalizedDecision(row)) }}
                    </button>
                  </div>
                </td>

                <td>
                  <div v-if="row.manual_status"
                       class="manual-status-pill"
                       :class="manualStatusClass(row.manual_status)">
                    {{ manualStatusLabel(row.manual_status) }}
                  </div>
                  <div v-else>—</div>
                </td>

                <td>
                  <span class="status-pill"
                        :class="row.is_processed ? 'processed' : 'pending'">
                    {{ row.is_processed ? 'Processed' : 'Pending' }}
                  </span>
                </td>

                <td>
                  <button class="notes-btn"
                          :class="row.has_notes ? 'has-notes' : 'no-notes'"
                          :disabled="!row.loan_id"
                          @click="openNotesModal(row)">
                    <i :class="row.has_notes ? 'fa-duotone fa-messages' : 'fa-duotone fa-message-plus'"></i>
                    <span>{{ row.has_notes ? 'View Notes' : 'Add Note' }}</span>
                  </button>
                </td>

                <td>
                  <button class="summary-btn"
                          :disabled="!getReasonSummary(row)"
                          @click="openReasonModal(row)">
                    Summary
                  </button>
                </td>

                <td>
                  <button class="polly-btn"
                          :disabled="!row.loan_id"
                          @click="openPolly(row)">
                    Open Polly
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="filteredRows.length" class="pagination-bar">
        <div class="pagination-info">
          Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredRows.length }}
        </div>

        <div class="pagination-actions">
          <button class="page-btn" @click="prevPage" :disabled="page === 1">Prev</button>
          <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" @click="nextPage" :disabled="page === totalPages">Next</button>
        </div>
      </div>

      <div v-if="showReasonModal" class="modal-backdrop" @click.self="closeReasonModal">
        <div class="modal-card reason-modal-card">
          <div class="modal-header">
            <h3 class="modal-title">Extension Summary</h3>
            <button class="close-btn" @click="closeReasonModal">✕</button>
          </div>

          <div class="modal-body reason-modal-body">
            <div class="reason-meta">
              <div><strong>Loan Number:</strong> {{ selectedRow.loan_number || '—' }}</div>
              <div><strong>Borrower:</strong> {{ borrowerName(selectedRow) }}</div>
              <div><strong>Recommendation:</strong> {{ decisionLabel(normalizedDecision(selectedRow)) }}</div>
              <div><strong>Extension Type:</strong> {{ extensionTypeLabel(selectedRow) }}</div>
              <div><strong>Extension Cost:</strong> {{ extensionCostDisplay(selectedRow) }}</div>
            </div>

            <div class="decision-help-text">
              Submitting sends the approved lock extension request to Polly. Rejecting only rejects this recommendation and prevents submit unless manually overridden.
            </div>

            <div class="reason-content">
              {{ getReasonSummary(selectedRow) || '—' }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="showDecisionActionModal" class="modal-backdrop" @click.self="closeDecisionActionModal">
        <div class="modal-card decision-action-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Lock Extension Recommendation</h3>
              <div class="notes-modal-subtitle">
                Loan {{ selectedManualRow.loan_number || '—' }} · {{ borrowerName(selectedManualRow) }}
              </div>
            </div>
            <button class="close-btn" @click="closeDecisionActionModal">✕</button>
          </div>

          <div class="modal-body decision-action-modal-body">
            <div class="reason-meta">
              <div><strong>Recommendation:</strong> {{ decisionLabel(normalizedDecision(selectedManualRow)) }}</div>
              <div><strong>Queue Status:</strong> {{ selectedManualRow.is_processed ? 'Processed' : 'Pending' }}</div>
              <div><strong>Manual Status:</strong> {{ manualStatusLabel(selectedManualRow.manual_status) || 'None' }}</div>
            </div>

            <div class="decision-detail-block">
              <div class="detail-label">Recommendation Summary</div>
              <div class="detail-value">
                {{ getReasonSummary(selectedManualRow) || 'No summary available.' }}
              </div>
            </div>

            <div v-if="actionErrorMessage" class="modal-action-error">
              {{ actionErrorMessage }}
            </div>

            <div class="decision-action-buttons multi-action">
              <button class="manual-approve-btn action-btn"
                      :disabled="!canSubmitApproved(selectedManualRow)"
                      @click="submitApprovedExtension(selectedManualRow)">
                {{ selectedManualRow.manual_status_loading ? 'Submitting...' : 'Submit Approved Request' }}
              </button>

              <button class="summary-btn action-btn"
                      :disabled="selectedManualRow.manual_status_loading"
                      @click="confirmDecisionAction('manual_review')">
                {{ selectedManualRow.manual_status_loading ? 'Saving...' : 'Mark Manual Review' }}
              </button>

              <button class="manual-clear-btn action-btn"
                      :disabled="!canRejectRecommendation(selectedManualRow)"
                      @click="rejectRecommendation(selectedManualRow)">
                {{ selectedManualRow.manual_status_loading ? 'Saving...' : 'Reject Recommendation' }}
              </button>

              <button class="page-btn action-btn"
                      :disabled="selectedManualRow.manual_status_loading"
                      @click="confirmDecisionAction('cleared')">
                {{ selectedManualRow.manual_status_loading ? 'Saving...' : 'Clear Status' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showNotesModal" class="modal-backdrop" @click.self="closeNotesModal">
        <div class="modal-card notes-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Extension Notes</h3>
              <div class="notes-modal-subtitle">
                Loan {{ selectedNotesRow.loan_number || '—' }} · {{ borrowerName(selectedNotesRow) }}
              </div>
            </div>
            <button class="close-btn" @click="closeNotesModal">✕</button>
          </div>

          <div class="modal-body notes-modal-body">
            <div class="notes-chat-shell">
              <div class="notes-chat-header">
                <div class="notes-chat-avatar">
                  <i class="fa-duotone fa-comments"></i>
                </div>
                <div class="notes-chat-header-text">
                  <div class="notes-chat-title">{{ borrowerName(selectedNotesRow) }}</div>
                  <div class="notes-chat-subtitle">Lock extension notes conversation</div>
                </div>
              </div>

              <div v-if="notesLoading" class="notes-state">Loading notes...</div>
              <div v-else-if="notesErrorMessage" class="notes-state error">{{ notesErrorMessage }}</div>

              <div v-else class="notes-thread">
                <div v-if="!notes.length" class="notes-empty">
                  <div class="notes-empty-icon">
                    <i class="fa-duotone fa-message-lines"></i>
                  </div>
                  <div class="notes-empty-title">No notes yet</div>
                  <div class="notes-empty-text">Start the conversation by adding the first note.</div>
                </div>

                <div v-for="(note, index) in notes"
                     :key="note.id || index"
                     class="chat-row"
                     :class="isOwnNote(note) ? 'own' : 'other'">
                  <div class="chat-meta" :class="isOwnNote(note) ? 'own' : 'other'">
                    <span class="chat-author">{{ getNoteAuthor(note) }}</span>
                    <span class="chat-time">{{ formatNoteDateTime(getNoteCreatedDate(note)) }}</span>
                  </div>

                  <div class="chat-bubble" :class="isOwnNote(note) ? 'own' : 'other'">
                    {{ getNoteText(note) }}
                  </div>
                </div>
              </div>

              <div class="notes-composer">
                <textarea v-model.trim="newNoteText"
                          class="notes-textarea"
                          rows="3"
                          placeholder="Type a note..."
                          :disabled="notesSaving"
                          @keydown.enter.exact.prevent="submitNote"></textarea>

                <div class="notes-composer-actions">
                  <div class="notes-compose-hint">Press Enter to send</div>
                  <button class="send-note-btn"
                          :disabled="notesSaving || !newNoteText || !selectedNotesRow.loan_id"
                          @click="submitNote">
                    {{ notesSaving ? 'Sending...' : 'Send Note' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showAltOrgModal" class="modal-backdrop" @click.self="closeAltOrgModal">
        <div class="modal-card alt-org-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Manage Lock Alt Orgs</h3>
              <div class="notes-modal-subtitle">Assign alt orgs by user</div>
            </div>
            <button class="close-btn" @click="closeAltOrgModal">✕</button>
          </div>

          <div class="modal-body alt-org-modal-body">
            <div class="alt-org-form-card">
              <div class="alt-org-form-grid">
                <div class="form-field user-picker-field">
                  <label class="field-label">User</label>
                  <input v-model.trim="altOrgUserSearch"
                         type="text"
                         class="search-input"
                         placeholder="Search display name" />
                  <select v-model="altOrgForm.azure_id" class="filter-select">
                    <option value="">Select user</option>
                    <option v-for="user in filteredImpersonableUsers"
                            :key="user.id || user.azureId || user.azure_id || user.userPrincipalName"
                            :value="user.id || user.azureId || user.azure_id">
                      {{ user.displayName }}
                    </option>
                  </select>
                </div>

                <div class="form-field">
                  <label class="field-label">Alt Org</label>
                  <input v-model.trim="altOrgForm.alt_org"
                         type="text"
                         class="search-input"
                         placeholder="Enter alt org" />
                </div>

                <div class="form-field">
                  <label class="field-label">Encompass Id</label>
                  <input v-model.trim="altOrgForm.encompass_id"
                         type="text"
                         class="search-input"
                         placeholder="Enter encompass id" />
                </div>
              </div>

              <div class="alt-org-form-actions">
                <button class="alt-org-save-btn"
                        :disabled="altOrgSaving || !altOrgForm.azure_id || !altOrgForm.alt_org"
                        @click="saveAltOrg">
                  {{ altOrgSaving ? 'Saving...' : (altOrgForm.id ? 'Update Alt Org' : 'Add Alt Org') }}
                </button>

                <button v-if="altOrgForm.id"
                        class="alt-org-cancel-btn"
                        :disabled="altOrgSaving"
                        @click="resetAltOrgForm">
                  Cancel Edit
                </button>
              </div>
            </div>

            <div v-if="altOrgErrorMessage" class="table-state error alt-org-error">
              {{ altOrgErrorMessage }}
            </div>

            <div class="alt-org-table-wrap">
              <table class="alt-org-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Azure Id</th>
                    <th>Alt Org</th>
                    <th>Encompass Id</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!altOrgRecords.length">
                    <td colspan="5" class="empty-cell">No alt org records found.</td>
                  </tr>
                  <tr v-for="record in altOrgRecords" :key="record.id">
                    <td>{{ getDisplayNameForAzureId(record.azure_id) }}</td>
                    <td>{{ record.azure_id || '—' }}</td>
                    <td>{{ record.alt_org || '—' }}</td>
                    <td>{{ record.encompass_id || '—' }}</td>
                    <td>
                      <div class="alt-org-row-actions">
                        <button class="summary-btn" @click="editAltOrg(record)">Edit</button>
                        <button class="manual-clear-btn" @click="deleteAltOrg(record)">Delete</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="not-authorized-card">
      <div class="not-authorized-icon">
        <i class="fa-duotone fa-lock"></i>
      </div>
      <div class="not-authorized-title">Not authorized</div>
      <div class="not-authorized-text">
        You do not have access to Lock Extensions Review.
      </div>
    </div>
  </div>
</template>

<script>
  import api from '@/api/api.service'
  import auth from "@/authConfig.js"
  import signalRService from "@/api/signalr.service"

  export default {
    name: 'LockExtensionsDashboard',
    data() {
      return {
        loading: false,
        errorMessage: '',
        rows: [],
        excludedLoanIds: [],
        lockDeskItemMap: {},
        timeFrame: 'today',
        search: '',
        decisionFilter: '',
        resolvedUserEmail: '',
        processedFilter: '',
        channelFilter: '',
        sortBy: 'request_date',
        sortDir: 'desc',
        page: 1,
        pageSize: 100,
        showReasonModal: false,
        selectedRow: {},
        showNotesModal: false,
        selectedNotesRow: {},
        showDecisionActionModal: false,
        selectedManualRow: {},
        notes: [],
        notesLoading: false,
        notesSaving: false,
        notesErrorMessage: '',
        newNoteText: '',
        showAltOrgModal: false,
        altOrgLoading: false,
        altOrgSaving: false,
        altOrgErrorMessage: '',
        altOrgRecords: [],
        fetchedUsers: [],
        filteredUsers: [],
        altOrgUserSearch: '',
        altOrgForm: {
          id: '',
          azure_id: '',
          alt_org: '',
          encompass_id: ''
        },
        pollyOrgId: 698,
        actionErrorMessage: '',
        actionSuccessMessage: '',
        submitDryRun: false
      }
    },
    computed: {
      roles() {
        const raw = localStorage.getItem("active_user_roles") || localStorage.getItem("user_roles")
        if (!raw) return []

        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            return parsed.map(x => String(x || "").toLowerCase().trim()).filter(Boolean)
          }

          return String(parsed || "")
            .split(",")
            .map(x => String(x || "").toLowerCase().trim())
            .filter(Boolean)
        } catch {
          return String(raw || "")
            .split(",")
            .map(x => String(x || "").toLowerCase().trim())
            .filter(Boolean)
        }
      },
      isAuthorized() {
        return this.roles.includes("compensation admin")
      },
      visibleRows() {
        return (this.rows || []).filter(x => this.matchesTimeFrame(this.getQueueDate(x)))
      },
      summary() {
        const rows = this.visibleRows || []

        return {
          total: rows.length,
          processed: rows.filter(x => !!x.is_processed).length,
          pending: rows.filter(x => !x.is_processed).length,
          approved: rows.filter(x => this.normalizedDecision(x) === 'approved').length,
          denied: rows.filter(x => this.normalizedDecision(x) === 'denied').length,
          manual_review: rows.filter(x => this.normalizedDecision(x) === 'manual_review').length,
          exception_needed: rows.filter(x => this.normalizedDecision(x) === 'exception_needed').length
        }
      },
      channelOptions() {
        return [...new Set(
          (this.visibleRows || [])
            .map(x => x.channel)
            .filter(Boolean)
        )].sort((a, b) => a.localeCompare(b))
      },
      filteredRows() {
        let rows = [...this.visibleRows]

        if (this.search) {
          const term = this.search.toLowerCase()

          rows = rows.filter(x => {
            const haystack = [
              x.loan_number,
              x.orgid,
              x.org_id,
              x.alt_org,
              x.encompass_id,
              x.loan_type,
              x.loan_purpose,
              x.loan_officer_name,
              x.loan_officer,
              x.loan_officer_email,
              x.borrower_first,
              x.borrower_last,
              x.borrower_first_name,
              x.borrower_last_name,
              x.borrower_name,
              x.lien_position,
              x.channel,
              x.decision,
              x.llm_decision,
              x.recommendation,
              x.status,
              x.manual_status,
              this.getReasonSummary(x),
              this.getRequestedExtensionDays(x),
              this.getCurrentExpiration(x),
              this.getNewExpiration(x)
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()

            return haystack.includes(term)
          })
        }

        if (this.decisionFilter) {
          rows = rows.filter(x => this.normalizedDecision(x) === this.decisionFilter)
        }

        if (this.processedFilter === 'processed') {
          rows = rows.filter(x => !!x.is_processed)
        }

        if (this.processedFilter === 'pending') {
          rows = rows.filter(x => !x.is_processed)
        }

        if (this.channelFilter) {
          rows = rows.filter(x => x.channel === this.channelFilter)
        }

        rows.sort((a, b) => {
          const aVal = this.sortValue(a, this.sortBy)
          const bVal = this.sortValue(b, this.sortBy)

          if (aVal === bVal) return 0
          if (aVal === null || aVal === undefined) return 1
          if (bVal === null || bVal === undefined) return -1

          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return this.sortDir === 'asc' ? aVal - bVal : bVal - aVal
          }

          const result = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
            sensitivity: 'base'
          })

          return this.sortDir === 'asc' ? result : -result
        })

        return rows
      },
      totalPages() {
        return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize))
      },
      pagedRows() {
        const start = (this.page - 1) * this.pageSize
        return this.filteredRows.slice(start, start + this.pageSize)
      },
      pageStart() {
        if (!this.filteredRows.length) return 0
        return (this.page - 1) * this.pageSize + 1
      },
      pageEnd() {
        return Math.min(this.page * this.pageSize, this.filteredRows.length)
      },
      currentAzureId() {
        return (
          localStorage.getItem('azure_id') ||
          localStorage.getItem('azureId') ||
          localStorage.getItem('active_user_azure_id') ||
          localStorage.getItem('user_azure_id') ||
          ''
        ).toString().trim().toLowerCase()
      },
      currentUserEmail() {
        return (
          localStorage.getItem('email') ||
          localStorage.getItem('user_email') ||
          localStorage.getItem('active_user_email') ||
          localStorage.getItem('preferred_username') ||
          ''
        ).toString().trim()
      },
      currentUserName() {
        // Prefer the authenticated account name. Fall back to '' (NOT 'You') — this value gets
        // persisted as completed_by/updated_by, and a viewer-relative "You" must never be stored.
        const account = auth.user && auth.user()
        if (account?.name) return account.name
        return (
          localStorage.getItem('full_name') ||
          localStorage.getItem('user_full_name') ||
          localStorage.getItem('display_name') ||
          localStorage.getItem('name') ||
          ''
        ).toString().trim()
      },
      filteredImpersonableUsers() {
        const term = String(this.altOrgUserSearch || '').toLowerCase().trim()
        if (!term) return this.filteredUsers

        return (this.filteredUsers || []).filter(user =>
          String(user.displayName || '').toLowerCase().includes(term)
        )
      }
    },
    watch: {
      search() {
        this.page = 1
      },
      decisionFilter() {
        this.page = 1
      },
      processedFilter() {
        this.page = 1
      },
      channelFilter() {
        this.page = 1
      },
      timeFrame() {
        this.page = 1
      },
      filteredRows() {
        if (this.page > this.totalPages) {
          this.page = 1
        }
      }
    },
    async mounted() {
      if (!this.isAuthorized) return
      const VALID_TIME_FRAMES = ['today','yesterday','this_week','last_week','last_7_days','last_30_days','last_90_days']
      if (this.$route?.query?.timeFrame && VALID_TIME_FRAMES.includes(this.$route.query.timeFrame)) {
        this.timeFrame = this.$route.query.timeFrame
      }
      if (this.$route?.query?.loan) {
        this.search = String(this.$route.query.loan)
      }
      try {
        const tokenResponse = await auth.acquireTokenResponse()
        this.resolvedUserEmail =
          tokenResponse?.account?.username ||
          tokenResponse?.account?.idTokenClaims?.email ||
          tokenResponse?.account?.idTokenClaims?.preferred_username ||
          ''
      } catch { /* non-fatal */ }
      await this.loadData()
      await this._connectLockExtensionHub()
    },
    beforeUnmount() {
      if (signalRService.stopLockExtensionConnection) {
        signalRService.stopLockExtensionConnection()
      }
    },
    methods: {
      canRejectRecommendation(row) {
        if (!row?.loan_id) return false
        if (row.manual_status_loading) return false
        if (row.manual_status === 'submitted') return false
        if (row.manual_status === 'rejected_by_user') return false

        return this.normalizedDecision(row) !== 'no_recommendation'
      },
      canSubmitApproved(row) {
        if (!row?.loan_id) return false
        if (row.manual_status_loading) return false
        if (row.manual_status === 'submitted') return false
        if (row.manual_status === 'rejected_by_user') return false

        return this.normalizedDecision(row) === 'approved'
      },
      getActionEmail() {
        return this.resolvedUserEmail || this.currentUserEmail || this.currentUserName || this.currentAzureId || 'unknown-user'
      },

      // Cost of the extension in basis points, or null when the prices didn't come through.
      //
      // Same rule the request page uses: market-based extensions are FREE, so 0 bps IS the
      // market-based case and any positive cost is at-cost. null is kept distinct from 0 on
      // purpose — missing price data must not render as "free".
      extensionCostBasisPoints(row) {
        const locked = Number(row?.locked_net_price)
        const requested = Number(row?.requested_net_price)

        if (Number.isFinite(locked) && Number.isFinite(requested)) {
          return Math.max(0, Math.round((locked - requested) * 100))
        }

        // Fall back to the precomputed delta. Absolute value because only its magnitude is
        // relied on here — the sign convention on that field isn't guaranteed.
        const difference = Number(row?.extension_price_difference)
        if (Number.isFinite(difference)) {
          return Math.max(0, Math.round(Math.abs(difference) * 100))
        }

        return null
      },

      // true / false / null (unknown).
      //
      // Prefers the system's OWN determination: on submit, the AI service writes
      // market_based_extension into the audit response_json. Rows that haven't been submitted don't
      // carry it, so those fall back to inferring from the prices — market-based extensions are
      // free, so 0 bps means market based.
      isMarketBasedExtension(row) {
        const parsed = this.parseJson(row?.response_json)

        if (typeof parsed?.market_based_extension === 'boolean') {
          return parsed.market_based_extension
        }

        const bps = this.extensionCostBasisPoints(row)
        return bps === null ? null : bps === 0
      },

      extensionTypeLabel(row) {
        const marketBased = this.isMarketBasedExtension(row)

        if (marketBased === null) return '—'
        return marketBased ? 'Market Based Extension' : 'Cost Based Extension'
      },

      extensionCostDisplay(row) {
        // Market based is free by definition, whatever the price fields happen to say.
        if (this.isMarketBasedExtension(row) === true) return 'No cost'

        const bps = this.extensionCostBasisPoints(row)

        if (bps === null) return '—'
        if (bps === 0) return 'No cost'

        const days = this.getNumericExtensionDays(row)
        return days > 0
          ? `${bps} bps over ${days} day${days === 1 ? '' : 's'}`
          : `${bps} bps`
      },

      getNumericExtensionDays(row) {
        const value = this.getRequestedExtensionDays(row)
        const parsed = Number(value)
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
      },

      async submitApprovedExtension(row) {
        if (!row?.loan_id) return

        const daysToExtend = this.getNumericExtensionDays(row)
        if (daysToExtend <= 0) {
          this.actionErrorMessage = 'Unable to submit. This row does not have a valid extension day value.'
          return
        }

        const previousStatus = row.manual_status || ''

        row.manual_status_loading = true
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const payload = {
            loan_id: row.loan_id,
            days_to_extend: daysToExtend,
            approved_by_azure_id: this.currentAzureId || '',
            approved_by_email: this.getActionEmail(),
            dry_run: false,
            force: false
          }

          const response = await api.SubmitApprovedLockExtension(payload, token)
          const result = response?.data?.data || response?.data || {}

          if (result.success === false) {
            throw new Error(result.error || result.message || 'Submit approved extension failed.')
          }

          row.manual_status = 'submitted'
          row.manual_reason = result.message || 'Submitted to Polly.'
          row.is_processed = true
          this._markLockDeskItemDone(row)

          if (result.effective_days_to_extend) {
            row.requested_extension_days = result.effective_days_to_extend
          }

          this.actionSuccessMessage = result.message || 'Lock extension request submitted.'
          await this.loadData()
          this.closeDecisionActionModal()
        } catch (e) {
          row.manual_status = previousStatus
          this.actionErrorMessage =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to submit approved extension.'
        } finally {
          row.manual_status_loading = false
        }
      },

      async rejectRecommendation(row) {
        if (!row?.loan_id) return

        const previousStatus = row.manual_status || ''

        row.manual_status_loading = true
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const payload = {
            loan_id: row.loan_id,
            rejected_by_azure_id: this.currentAzureId || '',
            rejected_by_email: this.getActionEmail(),
            reason: row.manual_reason || 'Rejected from Lock Extensions Review dashboard.'
          }

          const response = await api.RejectLockExtensionRecommendation(payload, token)
          const result = response?.data?.data || response?.data || {}

          if (result.success === false) {
            throw new Error(result.error || result.message || 'Reject recommendation failed.')
          }

          row.manual_status = 'rejected_by_user'
          row.manual_reason = result.manual_reason || payload.reason

          this.actionSuccessMessage = result.message || 'Recommendation rejected.'
          await this.loadData()
          this.closeDecisionActionModal()
        } catch (e) {
          row.manual_status = previousStatus
          this.actionErrorMessage =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to reject recommendation.'
        } finally {
          row.manual_status_loading = false
        }
      },
      async _connectLockExtensionHub() {
        try {
          if (signalRService.startLockExtensionConnection) {
            await signalRService.startLockExtensionConnection()
          } else {
            await signalRService.startRepriceConnection()
          }

          signalRService.onLockExtensionQueueItemProcessed((payload) => {
            const payloadQueueId = payload?.extensionQueueId || payload?.queueId
            if (payloadQueueId) {
              const row = this.rows.find(r => String(this.getQueueId(r) || '') === String(payloadQueueId))
              if (row) {
                row.is_processed = true
                this._markLockDeskItemDone(row)
              }
            }
            this.loadData()
          })
        } catch (err) {
          console.error("SignalR: failed to start LockExtensionHub connection", err)
        }
      },
      getQueueId(row) {
        return row?.id || row?.lock_extension_pending_approval_id || row?.extension_queue_id || row?.lock_extension_queue_id || ''
      },
      getQueueDate(row) {
        return row?.created_utc || row?.created_date || row?.request_date || null
      },
      getCurrentExpiration(row) {
        return row?.current_lock_expiration || row?.currentLockExpiration || row?.locked_expiration || null
      },

      getNewExpiration(row) {
        return row?.new_expiration || row?.newExpiration || row?.requested_expiration || null
      },
      getRequestedExtensionDays(row) {
        return row?.requested_extension_days ?? row?.lock_period ?? '—'
      },
      displayChannel(value) {
        if (!value) return '—'
        const normalized = String(value).trim().toLowerCase()
        if (normalized === 'banked - wholesale' || normalized === 'banked-wholesale' || normalized === 'banked wholesale') return 'Wholesale'
        if (normalized === 'banked - retail' || normalized === 'banked-retail' || normalized === 'banked retail') return 'Retail'
        return value
      },
      getRequestDays() {
        if (this.timeFrame === 'today') return 2
        if (this.timeFrame === 'yesterday') return 2
        if (this.timeFrame === 'this_week') return 7
        if (this.timeFrame === 'last_week') return 14
        if (this.timeFrame === 'last_7_days') return 7
        if (this.timeFrame === 'last_30_days') return 30
        if (this.timeFrame === 'last_90_days') return 90
        return 7
      },
      startOfDay(date) {
        const d = new Date(date)
        d.setHours(0, 0, 0, 0)
        return d
      },
      endOfDay(date) {
        const d = new Date(date)
        d.setHours(23, 59, 59, 999)
        return d
      },
      startOfWeek(date) {
        const d = this.startOfDay(date)
        const day = d.getDay()
        const diff = day === 0 ? -6 : 1 - day
        d.setDate(d.getDate() + diff)
        return d
      },
      endOfWeek(date) {
        const d = this.startOfWeek(date)
        d.setDate(d.getDate() + 6)
        d.setHours(23, 59, 59, 999)
        return d
      },
      matchesTimeFrame(value) {
        if (!value) return false

        const rowDate = new Date(value)
        if (Number.isNaN(rowDate.getTime())) return false

        const now = new Date()
        const todayStart = this.startOfDay(now)
        const todayEnd = this.endOfDay(now)

        if (this.timeFrame === 'today') {
          return rowDate >= todayStart && rowDate <= todayEnd
        }

        if (this.timeFrame === 'yesterday') {
          const yesterday = new Date(todayStart)
          yesterday.setDate(yesterday.getDate() - 1)
          const yesterdayStart = this.startOfDay(yesterday)
          const yesterdayEnd = this.endOfDay(yesterday)
          return rowDate >= yesterdayStart && rowDate <= yesterdayEnd
        }

        if (this.timeFrame === 'this_week') {
          const weekStart = this.startOfWeek(now)
          const weekEnd = this.endOfWeek(now)
          return rowDate >= weekStart && rowDate <= weekEnd
        }

        if (this.timeFrame === 'last_week') {
          const thisWeekStart = this.startOfWeek(now)
          const lastWeekStart = new Date(thisWeekStart)
          lastWeekStart.setDate(lastWeekStart.getDate() - 7)
          const lastWeekEnd = new Date(thisWeekStart)
          lastWeekEnd.setMilliseconds(-1)
          return rowDate >= lastWeekStart && rowDate <= lastWeekEnd
        }

        if (this.timeFrame === 'last_7_days') {
          const start = this.startOfDay(now)
          start.setDate(start.getDate() - 6)
          return rowDate >= start && rowDate <= todayEnd
        }

        if (this.timeFrame === 'last_30_days') {
          const start = this.startOfDay(now)
          start.setDate(start.getDate() - 29)
          return rowDate >= start && rowDate <= todayEnd
        }

        if (this.timeFrame === 'last_90_days') {
          const start = this.startOfDay(now)
          start.setDate(start.getDate() - 89)
          return rowDate >= start && rowDate <= todayEnd
        }

        return true
      },
      async loadData() {
        this.loading = true
        this.errorMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const getRows = api.GetLockExtensionsNeedingReview(this.getRequestDays(), token)
          const getExclusions = api.GetLockExtensionExclusions(token)
          const getManuals = api.GetLockExtensionManualStatuses(token)

          const [rowsResponse, exclusionsResponse, manualStatusesResponse] = await Promise.all([
            getRows,
            getExclusions.catch(() => ({ data: { data: [] } })),
            getManuals.catch(() => ({ data: { data: [] } }))
          ])

          const rowsData = Array.isArray(rowsResponse?.data?.data)
            ? rowsResponse.data.data
            : (Array.isArray(rowsResponse?.data) ? rowsResponse.data : [])

          const exclusions = Array.isArray(exclusionsResponse?.data?.data)
            ? exclusionsResponse.data.data
            : (Array.isArray(exclusionsResponse?.data) ? exclusionsResponse.data : [])

          const excludedLoanIds = exclusions
            .map(x => String(x.loan_id || x.loanId || '').toLowerCase())
            .filter(Boolean)

          const manualStatuses = Array.isArray(manualStatusesResponse?.data?.data)
            ? manualStatusesResponse.data.data
            : (Array.isArray(manualStatusesResponse?.data) ? manualStatusesResponse.data : [])

          const manualStatusMap = {}

          manualStatuses.forEach(item => {
            const keys = [
              item.lock_extension_pending_approval_id,
              item.extension_queue_id,
              item.lock_extension_queue_id,
              item.queue_id,
              item.loan_id,
              item.loanId,
              item.id
            ]
              .map(x => String(x || '').toLowerCase())
              .filter(Boolean)

            keys.forEach(key => {
              if (!manualStatusMap[key]) {
                manualStatusMap[key] = item
                return
              }

              const currentTime = new Date(item.updated_date || item.created_date || 0).getTime()
              const existingTime = new Date(
                manualStatusMap[key].updated_date || manualStatusMap[key].created_date || 0
              ).getTime()

              if (currentTime >= existingTime) {
                manualStatusMap[key] = item
              }
            })
          })

          this.excludedLoanIds = excludedLoanIds

          this.rows = rowsData.map(x => {
            const queueId = String(this.getQueueId(x) || '').toLowerCase()
            const loanId = String(x.loan_id || x.loanId || '').toLowerCase()
            const manual = manualStatusMap[queueId] || manualStatusMap[loanId] || {}

            return {
              ...x,
              has_notes: !!x.has_notes,
              is_excluded: excludedLoanIds.includes(String(x.loan_id || '').toLowerCase()),
              exclude_loading: false,
              manual_status_loading: false,
              manual_status: manual.manual_status || x.manual_status || '',
              manual_reason: manual.manual_reason || x.manual_reason || ''
            }
          })
          this._syncLockDeskQueue()
        } catch (e) {
          this.rows = []
          this.errorMessage =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to load lock extension requests.'
        } finally {
          this.loading = false
        }
      },
      borrowerName(row) {
        const fullName = row?.borrower_name || ''
        if (fullName) return fullName

        const first = row?.borrower_first || row?.borrower_first_name || ''
        const last = row?.borrower_last || row?.borrower_last_name || ''
        const full = `${first} ${last}`.trim()

        return full || '—'
      },
      normalizedDecision(row) {
        const raw =
          row?.recommendation ??
          row?.decision ??
          row?.llm_decision ??
          row?.review_result ??
          ''

        const value = String(raw).trim().toLowerCase()

        if (value === 'approve' || value === 'approved') return 'approved'
        if (value === 'deny' || value === 'denied') return 'denied'
        if (value === 'manual_review' || value === 'manual review' || value === 'review') return 'manual_review'
        if (value === 'exception_needed' || value === 'exception needed' || value === 'exception') return 'exception_needed'
        if (value === 'request_only' || value === 'request only') return 'request_only'
        if (!value) return 'no_recommendation'

        return value
      },
      decisionLabel(decision) {
        if (decision === 'approved') return 'Approve'
        if (decision === 'denied') return 'Deny'
        if (decision === 'manual_review') return 'Manual Review'
        if (decision === 'exception_needed') return 'Exception Needed'
        if (decision === 'request_only') return 'Request Only'
        if (decision === 'no_audit' || decision === 'no_recommendation') return 'No Recommendation Yet'
        return decision || '—'
      },
      decisionClass(decision) {
        if (decision === 'approved') return 'approve'
        if (decision === 'denied') return 'denied'
        if (decision === 'manual_review') return 'manual-review'
        if (decision === 'exception_needed') return 'exception-needed'
        if (decision === 'request_only') return 'request-only'
        return 'no-audit'
      },
      manualStatusClass(status) {
        if (status === 'approved' || status === 'approved_by_user') return 'approved'
        if (status === 'submitted') return 'approved'
        if (status === 'denied' || status === 'rejected_by_user') return 'cleared'
        if (status === 'manual_review') return 'manual_review'
        if (status === 'cleared') return 'cleared'
        return status || ''
      },

      manualStatusLabel(status) {
        if (status === 'approved') return 'Manually Approved'
        if (status === 'approved_by_user') return 'Approved by User'
        if (status === 'submitted') return 'Submitted'
        if (status === 'denied') return 'Manually Denied'
        if (status === 'rejected_by_user') return 'Rejected by User'
        if (status === 'manual_review') return 'Manual Review'
        if (status === 'cleared') return 'Override Cleared'
        return status || ''
      },
      formatDateTime(value) {
        if (!value) return '—'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return '—'
        return date.toLocaleDateString('en-US')
      },
      formatNoteDateTime(value) {
        if (!value) return ''
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return ''
        return date.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      },
      sortValue(row, key) {
        if (key === 'decision') return this.normalizedDecision(row)
        if (key === 'borrower_last_name') return this.borrowerName(row)
        if (key === 'is_processed') return row.is_processed ? 1 : 0
        if (key === 'has_notes') return row.has_notes ? 1 : 0
        if (key === 'manual_status') return row.manual_status || ''
        if (key === 'request_date') return new Date(this.getQueueDate(row) || 0).getTime()
        if (key === 'current_lock_expiration') return new Date(this.getCurrentExpiration(row) || 0).getTime()
        if (key === 'new_expiration') return new Date(this.getNewExpiration(row) || 0).getTime()
        if (key === 'requested_extension_days') return Number(this.getRequestedExtensionDays(row)) || 0
        return row[key]
      },
      setSort(key) {
        if (this.sortBy === key) {
          this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
          return
        }

        this.sortBy = key
        this.sortDir = 'asc'
      },
      sortIcon(key) {
        if (this.sortBy !== key) return ''
        return this.sortDir === 'asc' ? '▲' : '▼'
      },
      prevPage() {
        if (this.page > 1) this.page--
      },
      nextPage() {
        if (this.page < this.totalPages) this.page++
      },
      async toggleExclude(row, checked) {
        if (!row?.loan_id) return

        const previous = !!row.is_excluded
        row.exclude_loading = true
        row.is_excluded = checked

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          if (checked) {
            await api.UpsertLockExtensionExclusion({ loan_id: row.loan_id }, token)
          } else {
            await api.DeleteLockExtensionExclusion(row.loan_id, token)
          }
        } catch {
          row.is_excluded = previous
        } finally {
          row.exclude_loading = false
        }
      },
      async setManualStatus(row, status) {
        const loanId = row?.loan_id || row?.loanId || ''

        if (!loanId) {
          this.actionErrorMessage = 'Unable to update manual status. This row does not have a loan id.'
          return
        }

        const previous = row.manual_status || ''
        row.manual_status_loading = true
        row.manual_status = status

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const payload = {
            lock_extension_pending_approval_id: null,
            loan_id: loanId,
            manual_status: status,
            manual_reason: row.manual_reason || '',
            updated_by: this.getActionEmail(),

            lockExtensionPendingApprovalId: null,
            loanId: loanId,
            manualStatus: status,
            manualReason: row.manual_reason || '',
            updatedBy: this.getActionEmail()
          }

          await api.UpdateLockExtensionManualStatus(payload, token)
          if (status === 'submitted' || status === 'denied') {
            this._markLockDeskItemDone(row)
          }
        } catch (e) {
          row.manual_status = previous
          this.actionErrorMessage =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to update manual status.'
        } finally {
          row.manual_status_loading = false
        }
      },
      async _syncLockDeskQueue() {
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const response = await api.GetLockDeskQueueItems(90, token)
          const items = Array.isArray(response?.data?.data)
            ? response.data.data
            : (Array.isArray(response?.data) ? response.data : [])

          // Only track OPEN items — a DONE ticket must NOT block re-creating a new one, so a loan
          // that needs another extension reappears in the queue once its prior ticket is solved out.
          const existingMap = {}
          const existingStatusMap = {}
          items.forEach(item => {
            if (item.lock_extension_queue_id && item.status !== 'done') {
              const key = String(item.lock_extension_queue_id).toLowerCase()
              existingMap[key] = item.id
              existingStatusMap[key] = item.status
            }
          })

          const TERMINAL_MANUAL_STATUS = ['submitted', 'denied']
          const syncCutoff = new Date()
          syncCutoff.setHours(0, 0, 0, 0)

          for (const row of this.rows) {
            const queueId = String(this.getQueueId(row) || '').toLowerCase()
            if (!queueId) continue

            // Only sync items created today
            const rowDate = new Date(row.created_utc || 0)
            if (rowDate < syncCutoff) continue

            // Skip submitted/denied items — close out any open queue entry, never create a new one
            if (TERMINAL_MANUAL_STATUS.includes(row.manual_status)) {
              if (existingMap[queueId] && existingStatusMap[queueId] !== 'done') {
                try {
                  await api.UpdateLockDeskQueueItemStatus({
                    id: existingMap[queueId],
                    status: 'done',
                    updated_by_azure_id: this.currentAzureId || '',
                    updated_by_name: this.currentUserName || ''
                  }, token)
                  existingStatusMap[queueId] = 'done'
                } catch {
                  // Non-fatal
                }
              }
              continue
            }

            if (row.is_excluded) continue
            // Only auto-add items the AI explicitly flagged as needing manual review
            if (this.normalizedDecision(row) !== 'manual_review') continue

            if (!existingMap[queueId]) {
              try {
                const res = await api.IntakeLockDeskQueueItem({
                  request_type: 'lock_extension',
                  loan_id: row.loan_id || null,
                  loan_number: row.loan_number || '',
                  borrower_first: row.borrower_first || row.borrower_first_name || '',
                  borrower_last: row.borrower_last || row.borrower_last_name || '',
                  loan_officer_name: row.loan_officer_name || row.loan_officer || '',
                  orgid: row.orgid || row.org_id || '',
                  channel: row.channel || '',
                  request_description: this.decisionLabel(this.normalizedDecision(row)),
                  created_by_azure_id: this.currentAzureId || '',
                  lock_extension_queue_id: queueId,
                  // Arrived = when the AI processed it (llm_created_utc), matching the auto-inject service —
                  // so the queue shows the same time whether the service or this sync created the row.
                  source_created_utc: row.llm_created_utc || row.created_utc || null
                }, token)
                const newItem = res?.data?.data || res?.data || {}
                if (newItem.id) existingMap[queueId] = newItem.id
              } catch {
                // Non-fatal — will retry on next load
              }
            }

            row._lockDeskItemId = existingMap[queueId] || null
          }

          this.lockDeskItemMap = existingMap
        } catch {
          // Non-fatal
        }
      },
      async _markLockDeskItemDone(row) {
        const itemId = row?._lockDeskItemId
        if (!itemId) return
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          await api.UpdateLockDeskQueueItemStatus({
            id: itemId,
            status: 'done',
            updated_by_azure_id: this.currentAzureId || '',
            updated_by_name: this.currentUserName || ''
          }, tokenResponse.accessToken)
        } catch {
          // Non-fatal
        }
      },
      openReasonModal(row) {
        this.selectedRow = row || {}
        this.showReasonModal = true
      },
      closeReasonModal() {
        this.showReasonModal = false
        this.selectedRow = {}
      },
      openDecisionActionModal(row) {
        this.selectedManualRow = row || {}
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''
        this.showDecisionActionModal = true
      },

      closeDecisionActionModal() {
        this.showDecisionActionModal = false
        this.selectedManualRow = {}
        this.actionErrorMessage = ''
        this.actionSuccessMessage = ''
      },
      async confirmDecisionAction(status) {
        if (!this.getQueueId(this.selectedManualRow)) return

        await this.setManualStatus(this.selectedManualRow, status)
        this.closeDecisionActionModal()
      },
      async openNotesModal(row) {
        this.selectedNotesRow = row || {}
        this.showNotesModal = true
        this.notes = []
        this.newNoteText = ''
        this.notesErrorMessage = ''
        await this.loadNotes()
      },
      closeNotesModal() {
        this.showNotesModal = false
        this.selectedNotesRow = {}
        this.notes = []
        this.newNoteText = ''
        this.notesLoading = false
        this.notesSaving = false
        this.notesErrorMessage = ''
      },
      async loadNotes() {
        if (!this.selectedNotesRow?.loan_id) return

        this.notesLoading = true
        this.notesErrorMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const response = await api.GetLockExtensionNotes(
            this.selectedNotesRow.loan_id,
            tokenResponse.accessToken
          )

          const notes = response?.data?.data?.notes || response?.data?.notes || response?.data?.data || response?.data || []

          this.notes = [...notes].sort((a, b) => {
            const aTime = new Date(this.getNoteCreatedDate(a) || 0).getTime()
            const bTime = new Date(this.getNoteCreatedDate(b) || 0).getTime()
            return aTime - bTime
          })
        } catch (e) {
          this.notes = []
          this.notesErrorMessage =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to load notes.'
        } finally {
          this.notesLoading = false
        }
      },
      async submitNote() {
        if (!this.newNoteText || !this.selectedNotesRow?.loan_id) return

        this.notesSaving = true
        this.notesErrorMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()

          const payload = {
            loan_id: this.selectedNotesRow.loan_id,
            note: this.newNoteText,
            created_by: this.currentAzureId || this.currentUserName,
            message_type: 'LockExtensions'
          }

          await api.SubmitLockExtensionNote(payload, tokenResponse.accessToken)

          this.newNoteText = ''
          this.updateRowHasNotes(this.selectedNotesRow.loan_id, true)
          await this.loadNotes()
        } catch (e) {
          this.notesErrorMessage =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to submit note.'
        } finally {
          this.notesSaving = false
        }
      },
      updateRowHasNotes(loanId, hasNotes) {
        this.rows = (this.rows || []).map(x => {
          if (x.loan_id === loanId) {
            return {
              ...x,
              has_notes: !!hasNotes
            }
          }

          return x
        })

        if (this.selectedNotesRow?.loan_id === loanId) {
          this.selectedNotesRow = {
            ...this.selectedNotesRow,
            has_notes: !!hasNotes
          }
        }
      },
      parseJson(json) {
        if (!json) return null

        try {
          return JSON.parse(json)
        } catch {
          return null
        }
      },
      getReasonSummary(row) {
        const parsed = this.parseJson(row?.response_json)
        return (
          parsed?.summary ||
          row?.llm_summary ||
          row?.recommendation_reason ||
          row?.reason ||
          row?.summary ||
          ''
        )
      },
      getNoteText(note) {
        return (
          note?.note ||
          note?.notes ||
          note?.message ||
          note?.text ||
          note?.note_text ||
          note?.body ||
          ''
        )
      },
      getNoteCreatedDate(note) {
        return (
          note?.created ||
          note?.created_date ||
          note?.createdDate ||
          note?.created_utc ||
          note?.createdUtc ||
          note?.updated_date ||
          note?.updatedDate ||
          null
        )
      },
      getNoteAuthor(note) {
        return (
          note?.created_by_name ||
          note?.createdByName ||
          note?.created_by ||
          note?.createdBy ||
          note?.user_name ||
          note?.userName ||
          note?.author ||
          'Lock Extension Note'
        )
      },
      isOwnNote(note) {
        const currentAzureId = String(this.currentAzureId || '').toLowerCase()
        const createdBy = String(note?.created_by || note?.createdBy || '').toLowerCase()
        const createdByAzureId = String(note?.created_by_azure_id || note?.createdByAzureId || '').toLowerCase()
        const createdByUserId = String(note?.azure_id || note?.azureId || '').toLowerCase()

        if (!currentAzureId) return false

        return (
          createdBy === currentAzureId ||
          createdByAzureId === currentAzureId ||
          createdByUserId === currentAzureId
        )
      },
      async openAltOrgModal() {
        this.showAltOrgModal = true
        this.altOrgErrorMessage = ''
        this.altOrgLoading = true

        try {
          await Promise.all([
            this.getImpersonateUsers(),
            this.loadAltOrgRecords()
          ])
        } catch (e) {
          this.altOrgErrorMessage =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to load alt org data.'
        } finally {
          this.altOrgLoading = false
        }
      },
      closeAltOrgModal() {
        this.showAltOrgModal = false
        this.altOrgErrorMessage = ''
        this.resetAltOrgForm()
      },
      resetAltOrgForm() {
        this.altOrgForm = {
          id: '',
          azure_id: '',
          alt_org: '',
          encompass_id: ''
        }
        this.altOrgUserSearch = ''
      },
      async getImpersonateUsers() {
        const tokenResponse = await auth.acquireTokenResponse()
        return api
          .GetImpersonableUsers(this.currentAzureId, tokenResponse.accessToken)
          .then(res => {
            if (res.data?.data) {
              const sorted = [...res.data.data].sort((a, b) =>
                String(a.displayName || '').localeCompare(String(b.displayName || ''))
              )
              this.fetchedUsers = sorted
              this.filteredUsers = sorted
            }
          })
      },
      async loadAltOrgRecords() {
        const tokenResponse = await auth.acquireTokenResponse()
        const response = await api.GetLockExtensionAltOrgs(tokenResponse.accessToken)
        this.altOrgRecords = response?.data?.data || []
      },
      getDisplayNameForAzureId(azureId) {
        const match = (this.fetchedUsers || []).find(x => {
          const id = x.id || x.azureId || x.azure_id
          return String(id || '').toLowerCase() === String(azureId || '').toLowerCase()
        })

        return match?.displayName || 'Unknown User'
      },
      editAltOrg(record) {
        this.altOrgForm = {
          id: record.id || '',
          azure_id: record.azure_id || '',
          alt_org: record.alt_org || '',
          encompass_id: record.encompass_id || ''
        }
      },
      async saveAltOrg() {
        if (!this.altOrgForm.azure_id || !this.altOrgForm.alt_org) return

        this.altOrgSaving = true
        this.altOrgErrorMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          await api.UpsertLockExtensionAltOrg(this.altOrgForm, tokenResponse.accessToken)
          await this.loadAltOrgRecords()
          this.resetAltOrgForm()
        } catch (e) {
          this.altOrgErrorMessage =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to save alt org.'
        } finally {
          this.altOrgSaving = false
        }
      },
      async deleteAltOrg(record) {
        if (!record?.id) return

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          await api.DeleteLockExtensionAltOrg(record.id, tokenResponse.accessToken)
          await this.loadAltOrgRecords()
        } catch (e) {
          this.altOrgErrorMessage =
            e?.response?.data?.message ||
            e?.message ||
            'Failed to delete alt org.'
        }
      },
      openPolly(row) {
        if (!row?.loan_id) return
        const url = `https://lx.pollyex.com/pe/loans/${row.loan_id}?mode=lock&orgId=${this.pollyOrgId}`
        window.open(url, '_blank')
      }
    }
  }
</script>

<style scoped>
  .repriced-locks-page {
    padding: 18px;
    background: #f7f8fb;
    min-height: calc(100vh - 90px);
    display: flex;
    flex-direction: column;
    color: #1f2a44;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
  }

  .page-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .page-icon {
    width: 48px;
    height: 48px;
    border-radius: 5px;
    background: #123a71;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex: 0 0 auto;
  }

  .page-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1f2a44;
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7b8397;
    font-weight: 500;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .summary-sections {
    display: grid;
    grid-template-columns: 1.1fr 1.4fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  .summary-section {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    padding: 12px;
  }

  .decisions-section {
    background: #fcfbff;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .section-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-icon {
    color: #5f79c7;
    font-size: 14px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 700;
    color: #28344f;
  }

  .section-cards {
    display: grid;
    gap: 10px;
  }

  .summary-section:first-child .section-cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .decisions-section .section-cards {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .summary-card {
    background: #fdfdfe;
    border: 1px solid #e8ebf2;
    border-radius: 5px;
    padding: 12px;
  }

    .summary-card.approve {
      border-color: #d8eedf;
      background: #fbfefc;
    }

    .summary-card.request-only {
      border-color: #f4e7c4;
      background: #fffdfa;
    }

    .summary-card.manual-review {
      border-color: #f2d7d9;
      background: #fffafb;
    }

    .summary-card.no-audit {
      border-color: #e8ebf2;
      background: #fbfbfd;
    }

    .summary-card.denied {
      border-color: #f1d9dc;
      background: #fffafb;
    }

    .summary-card.exception-needed {
      border-color: #efe6d2;
      background: #fffdf8;
    }

  .summary-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .summary-icon {
    font-size: 13px;
    color: #6a7b9f;
  }

  .summary-label {
    font-size: 12px;
    color: #7b8397;
    font-weight: 600;
  }

  .summary-value {
    font-size: 22px;
    font-weight: 800;
    color: #1f2a44;
    line-height: 1.1;
  }

  .toolbar {
    margin-bottom: 10px;
  }

  .single-line-toolbar {
    display: grid;
    grid-template-columns: minmax(320px, 1fr) 190px 190px 190px;
    gap: 8px;
    align-items: center;
  }

  .search-input,
  .filter-select {
    height: 38px;
    border: 1px solid #dfe4ee;
    background: #fcfcfe;
    border-radius: 5px;
    padding: 0 14px;
    font-size: 13px;
    width: 100%;
    color: #23314f;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

    .search-input::placeholder {
      color: #94a0b8;
    }

    .search-input:focus,
    .filter-select:focus,
    .notes-textarea:focus {
      border-color: #cfd8e8;
      box-shadow: 0 0 0 2px rgba(49, 93, 181, 0.08);
    }

  .days-select {
    min-width: 140px;
  }

  .refresh-btn,
  .page-btn,
  .close-btn,
  .summary-btn,
  .polly-btn,
  .notes-btn,
  .send-note-btn,
  .alt-orgs-btn,
  .alt-org-save-btn,
  .alt-org-cancel-btn,
  .manual-approve-btn,
  .manual-clear-btn,
  .decision-pill-button {
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #22314f;
    height: 38px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  }

    .refresh-btn:hover,
    .page-btn:hover,
    .summary-btn:hover,
    .polly-btn:hover,
    .notes-btn:hover,
    .send-note-btn:hover,
    .alt-orgs-btn:hover,
    .alt-org-save-btn:hover,
    .alt-org-cancel-btn:hover,
    .manual-approve-btn:hover,
    .manual-clear-btn:hover,
    .decision-pill-button:hover {
      background: #f4f7fc;
      border-color: #cfd8e8;
    }

  .alt-orgs-btn {
    color: #315db5;
    background: #f5f8fc;
    border-color: #d9e2f0;
  }

  .summary-btn,
  .polly-btn,
  .notes-btn,
  .manual-approve-btn,
  .manual-clear-btn {
    height: 30px;
    padding: 0 12px;
    font-size: 12px;
  }

  .summary-btn {
    color: #315db5;
    background: #f5f8fc;
    border-color: #d9e2f0;
  }

  .polly-btn {
    color: #0f766e;
    background: #f3fbf9;
    border-color: #cfe9e2;
  }

  .notes-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
  }

    .notes-btn.has-notes {
      color: #315db5;
      background: #eef4ff;
      border-color: #d7e5fd;
    }

    .notes-btn.no-notes {
      color: #8a5a14;
      background: #fff7e8;
      border-color: #f3dfb7;
    }

  .send-note-btn,
  .alt-org-save-btn {
    background: #0b84ff;
    border-color: #0b84ff;
    color: #ffffff;
    min-width: 110px;
  }

    .send-note-btn:hover,
    .alt-org-save-btn:hover {
      background: #0677e6;
      border-color: #0677e6;
    }

  .alt-org-cancel-btn {
    color: #6b7486;
    background: #f5f6f8;
    border-color: #dde2eb;
  }

  .manual-approve-btn {
    color: #2c7a4b;
    background: #eef9f0;
    border-color: #d9eedf;
  }

  .manual-clear-btn {
    color: #9a6b11;
    background: #fff7e7;
    border-color: #f5e3b5;
  }

    .refresh-btn:disabled,
    .page-btn:disabled,
    .summary-btn:disabled,
    .polly-btn:disabled,
    .notes-btn:disabled,
    .send-note-btn:disabled,
    .alt-orgs-btn:disabled,
    .alt-org-save-btn:disabled,
    .alt-org-cancel-btn:disabled,
    .manual-approve-btn:disabled,
    .manual-clear-btn:disabled,
    .decision-pill-button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      transform: none;
    }

  .table-card {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: none;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .table-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid #e9edf3;
    background: #fafbfd;
  }

  .table-card-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .table-card-icon {
    color: #5f79c7;
    font-size: 14px;
  }

  .table-card-title {
    font-size: 14px;
    font-weight: 700;
    color: #28344f;
  }

  .table-card-count {
    font-size: 13px;
    font-weight: 700;
    color: #7b8397;
  }

  .table-wrap {
    overflow: auto;
    flex: 1;
  }

  .locks-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 1850px;
  }

    .locks-table th,
    .locks-table td {
      padding: 12px 12px;
      border-bottom: 1px solid #edf0f5;
      font-size: 13px;
      text-align: left;
      vertical-align: middle;
    }

    .locks-table th {
      background: #f7f9fc;
      color: #6f7b92;
      font-weight: 700;
      white-space: nowrap;
      cursor: pointer;
      position: sticky;
      top: 0;
      z-index: 1;
    }

    .locks-table tbody tr {
      background: #fbfbfd;
    }

      .locks-table tbody tr:hover {
        background: #f8faff;
      }

  .excluded-row {
    opacity: 0.62;
  }

  .exclude-check-wrap {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .exclude-check-label {
    font-size: 11px;
    color: #7b8397;
    font-weight: 700;
  }

  .primary-text {
    font-weight: 700;
    color: #24314f;
  }

  .decision-pill,
  .status-pill,
  .manual-status-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    border: 1px solid transparent;
  }

  .decision-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }

  .decision-pill-button {
    cursor: pointer;
    height: auto;
    min-height: 28px;
    padding: 4px 10px;
  }

  .decision-pill.approve {
    background: #eef9f0;
    border-color: #d9eedf;
    color: #2c7a4b;
  }

  .decision-pill.request-only {
    background: #fff7e7;
    border-color: #f5e3b5;
    color: #9a6b11;
  }

  .decision-pill.manual-review {
    background: #fff0f1;
    border-color: #f0d1d5;
    color: #b24553;
  }

  .decision-pill.no-audit {
    background: #f2f4f7;
    border-color: #e1e5ec;
    color: #6d7789;
  }

  .status-pill.processed {
    background: #edf4ff;
    border: 1px solid #d7e5fd;
    color: #3d6fd1;
  }

  .status-pill.pending {
    background: #f2f4f7;
    border: 1px solid #e1e5ec;
    color: #6d7789;
  }

  .manual-status-pill.approved {
    background: #eef9f0;
    border-color: #d9eedf;
    color: #2c7a4b;
  }

  .manual-status-pill.cleared {
    background: #edf4ff;
    border-color: #d7e5fd;
    color: #3d6fd1;
  }

  .table-state {
    padding: 28px;
    text-align: center;
    color: #7b8397;
    font-size: 14px;
  }

    .table-state.error {
      color: #b94b5c;
    }

  .pagination-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 4px 0;
    gap: 12px;
  }

  .pagination-info {
    color: #7b8397;
    font-size: 13px;
    font-weight: 500;
  }

  .pagination-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .page-number {
    font-size: 13px;
    color: #5c6780;
    font-weight: 600;
  }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(28, 37, 56, 0.32);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 1000;
  }

  .modal-card {
    width: min(760px, 100%);
    max-height: 85vh;
    background: #fcfcfe;
    border: 1px solid #e7ebf2;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 18px 44px rgba(28, 37, 56, 0.12);
  }

  .modal-action-error {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fee2e2;
    color: #991b1b;
    font-size: 13px;
    font-weight: 600;
  }

  .modal-action-success {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #dcfce7;
    color: #166534;
    font-size: 13px;
    font-weight: 600;
  }

  .notes-modal-card {
    width: min(720px, 100%);
  }

  .alt-org-modal-card {
    width: min(980px, 100%);
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid #e9edf3;
    background: #fafbfd;
  }

  .modal-title {
    margin: 0;
    font-size: 16px;
    color: #22314f;
    font-weight: 700;
  }

  .notes-modal-subtitle {
    margin-top: 4px;
    font-size: 12px;
    color: #7b8397;
    font-weight: 600;
  }

  .close-btn {
    width: 36px;
    height: 36px;
    padding: 0;
  }

  .reason-modal-body,
  .decision-action-modal-body {
    padding: 18px;
  }

  .reason-meta {
    display: grid;
    gap: 6px;
    margin-bottom: 14px;
    color: #5f6b84;
    font-size: 13px;
  }

  .reason-content {
    font-size: 13px;
    line-height: 1.65;
    color: #24314f;
    white-space: pre-wrap;
    word-break: break-word;
    background: #f7f9fc;
    border: 1px solid #e6ebf2;
    border-radius: 5px;
    padding: 14px;
  }

  .decision-action-buttons {
    display: flex;
    gap: 10px;
    margin-top: 16px;
  }

  .action-btn {
    height: 38px;
    min-width: 180px;
  }

  .notes-modal-body {
    padding: 0;
    background: #e9eef5;
  }

  .notes-chat-shell {
    display: flex;
    flex-direction: column;
    max-height: calc(85vh - 70px);
    min-height: 620px;
    background: linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.55)), radial-gradient(circle at top left, #f8fbff 0, #edf2f8 60%, #e6ecf4 100%);
  }

  .notes-chat-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(202, 212, 226, 0.9);
    background: rgba(248, 250, 253, 0.92);
    backdrop-filter: blur(8px);
  }

  .notes-chat-avatar {
    width: 42px;
    height: 42px;
    border-radius: 999px;
    background: linear-gradient(180deg, #d9e7ff 0%, #bdd4ff 100%);
    color: #2859b8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    border: 1px solid #c9daf8;
  }

  .notes-chat-header-text {
    display: flex;
    flex-direction: column;
  }

  .notes-chat-title {
    font-size: 14px;
    font-weight: 700;
    color: #24314f;
  }

  .notes-chat-subtitle {
    font-size: 12px;
    color: #7b8397;
    margin-top: 2px;
  }

  .notes-state {
    padding: 24px;
    text-align: center;
    color: #6a7488;
    font-size: 14px;
  }

    .notes-state.error {
      color: #b94b5c;
    }

  .notes-thread {
    flex: 1;
    overflow-y: auto;
    padding: 18px 14px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .notes-empty {
    margin: auto;
    text-align: center;
    color: #6f7b92;
    max-width: 280px;
  }

  .notes-empty-icon {
    width: 58px;
    height: 58px;
    border-radius: 999px;
    margin: 0 auto 12px;
    background: #f6f9fd;
    border: 1px solid #dde6f2;
    color: #5d7ac2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
  }

  .notes-empty-title {
    font-size: 16px;
    font-weight: 700;
    color: #26324d;
    margin-bottom: 6px;
  }

  .notes-empty-text {
    font-size: 13px;
    line-height: 1.5;
  }

  .chat-row {
    display: flex;
    flex-direction: column;
    max-width: 78%;
  }

    .chat-row.own {
      align-self: flex-end;
    }

    .chat-row.other {
      align-self: flex-start;
    }

  .chat-meta {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
    font-size: 11px;
    color: #79859a;
    padding: 0 6px;
  }

    .chat-meta.own {
      justify-content: flex-end;
    }

    .chat-meta.other {
      justify-content: flex-start;
    }

  .chat-author {
    font-weight: 700;
  }

  .chat-bubble {
    position: relative;
    border-radius: 20px;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 1.45;
    word-break: break-word;
    box-shadow: 0 1px 1px rgba(17, 24, 39, 0.06);
  }

    .chat-bubble.own {
      background: #0b84ff;
      color: #ffffff;
      border-bottom-right-radius: 6px;
    }

    .chat-bubble.other {
      background: #ffffff;
      color: #22314f;
      border: 1px solid #e1e8f2;
      border-bottom-left-radius: 6px;
    }

  .notes-composer {
    padding: 14px;
    border-top: 1px solid rgba(202, 212, 226, 0.9);
    background: rgba(249, 251, 254, 0.96);
  }

  .notes-textarea {
    width: 100%;
    resize: none;
    border: 1px solid #d8e1ee;
    border-radius: 16px;
    padding: 12px 14px;
    font-size: 13px;
    line-height: 1.4;
    background: #ffffff;
    color: #22314f;
    outline: none;
  }

  .notes-composer-actions {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .notes-compose-hint {
    font-size: 12px;
    color: #8a94a8;
  }

  .alt-org-modal-body {
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .alt-org-form-card {
    background: #f8fafc;
    border: 1px solid #e6ebf2;
    border-radius: 8px;
    padding: 16px;
  }

  .alt-org-form-grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: 12px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .field-label {
    font-size: 12px;
    font-weight: 700;
    color: #5f6b84;
  }

  .user-picker-field {
    min-width: 0;
  }

  .alt-org-form-actions {
    display: flex;
    gap: 8px;
    margin-top: 14px;
  }

  .alt-org-table-wrap {
    max-height: 420px;
    overflow: auto;
    border: 1px solid #e6ebf2;
    border-radius: 8px;
    background: #ffffff;
  }

  .alt-org-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 700px;
  }

    .alt-org-table th,
    .alt-org-table td {
      padding: 12px;
      border-bottom: 1px solid #edf0f5;
      font-size: 13px;
      text-align: left;
    }

    .alt-org-table th {
      background: #f7f9fc;
      color: #6f7b92;
      font-weight: 700;
      position: sticky;
      top: 0;
    }

  .alt-org-row-actions {
    display: flex;
    gap: 8px;
  }

  .empty-cell {
    text-align: center;
    color: #7b8397;
    padding: 24px;
  }

  .alt-org-error {
    padding: 12px 0 0;
  }

  @media (max-width: 1600px) {
    .summary-sections {
      grid-template-columns: 1fr;
    }

    .summary-section:first-child .section-cards {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .decisions-section .section-cards {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  @media (max-width: 1200px) {
    .single-line-toolbar {
      grid-template-columns: 1fr 1fr;
    }

    .summary-section:first-child .section-cards,
    .decisions-section .section-cards,
    .alt-org-form-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 900px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
    }

    .header-actions {
      width: 100%;
      flex-wrap: wrap;
    }

    .notes-chat-shell {
      min-height: 540px;
    }

    .alt-org-form-grid {
      grid-template-columns: 1fr;
    }

    .decision-action-buttons {
      flex-direction: column;
    }
  }

  @media (max-width: 700px) {
    .repriced-locks-page {
      padding: 16px;
    }

    .single-line-toolbar {
      grid-template-columns: 1fr;
    }

    .summary-section:first-child .section-cards,
    .decisions-section .section-cards {
      grid-template-columns: 1fr;
    }

    .pagination-bar {
      flex-direction: column;
      align-items: flex-start;
    }

    .chat-row {
      max-width: 90%;
    }

    .notes-composer-actions,
    .alt-org-form-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .not-authorized-card {
    min-height: calc(100vh - 140px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    color: #24314f;
  }

  .not-authorized-icon {
    width: 52px;
    height: 52px;
    border-radius: 5px;
    background: #f4f7fc;
    border: 1px solid #dbe4f1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #315db5;
    font-size: 20px;
  }

  .not-authorized-title {
    font-size: 20px;
    font-weight: 700;
    color: #22314f;
  }

  .not-authorized-text {
    font-size: 14px;
    color: #7b8397;
  }

  .compact-cell {
    white-space: nowrap;
  }

  .decision-action-modal-card {
    width: min(720px, calc(100vw - 32px));
    max-width: 720px;
  }

  .decision-action-buttons.multi-action {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .decision-action-buttons .action-btn {
    width: 100%;
    min-height: 42px;
    white-space: normal;
    line-height: 1.15;
    text-align: center;
  }

  .decision-detail-block {
    margin-bottom: 14px;
    padding: 12px;
    border: 1px solid #e6ebf2;
    border-radius: 8px;
    background: #f7f9fc;
  }

  .detail-label {
    font-size: 12px;
    font-weight: 700;
    color: #6f7b92;
    margin-bottom: 6px;
  }

  .detail-value {
    font-size: 13px;
    line-height: 1.55;
    color: #24314f;
    white-space: pre-wrap;
  }

  .decision-pill.exception-needed {
    background: #fff7e7;
    border-color: #f5e3b5;
    color: #9a6b11;
  }

  .decision-pill.denied {
    background: #fff0f1;
    border-color: #f0d1d5;
    color: #b24553;
  }

  .decision-help-text {
    margin-bottom: 14px;
    font-size: 12px;
    line-height: 1.45;
    color: #6f7b92;
  }

  @media (max-width: 640px) {
    .decision-action-buttons.multi-action {
      grid-template-columns: 1fr;
    }
  }
</style>
